import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CloudUpload, Paperclip, Trash2 } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { useSnackbar } from "notistack";
import { uploadAsset } from "../api/ess";
import { apiErrorMessage } from "../utils/apiError";

export interface UploadedFile {
  asset_id: string;
  name: string;
  size: number;
  mime?: string;
  previewUrl?: string;
}

interface Props {
  files: UploadedFile[];
  onChange: (next: UploadedFile[]) => void;
  accept?: string;
  maxSizeMb?: number;
  /**
   * "button" — the full outlined "Add attachments" CTA (default).
   * "icon" — a compact paperclip IconButton, for use inside the notes
   * composer toolbar where the trigger sits alongside the Add Note CTA.
   */
  variant?: "button" | "icon";
}

function isImage(file: Pick<UploadedFile, "mime" | "name">) {
  if (file.mime?.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name);
}

export default function AttachmentUploader({
  files,
  onChange,
  accept,
  maxSizeMb = 10,
  variant = "button",
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    return () => {
      files.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePick = () => inputRef.current?.click();

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      const next: UploadedFile[] = [...files];
      for (const f of Array.from(fileList)) {
        if (f.size > maxSizeMb * 1024 * 1024) {
          enqueueSnackbar(`${f.name} is larger than ${maxSizeMb} MB`, {
            variant: "warning",
          });
          continue;
        }
        const { asset_id } = await uploadAsset(f);
        const previewUrl = f.type.startsWith("image/")
          ? URL.createObjectURL(f)
          : undefined;
        next.push({
          asset_id,
          name: f.name,
          size: f.size,
          mime: f.type,
          previewUrl,
        });
      }
      onChange(next);
    } catch (err) {
      enqueueSnackbar(apiErrorMessage(err, "Upload failed"), {
        variant: "error",
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx: number) => {
    const removed = files[idx];
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    onChange(files.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />
      {variant === "icon" ? (
        <Tooltip title="Attach files">
          <span>
            <IconButton
              onClick={handlePick}
              disabled={uploading}
              aria-label="Attach files"
              sx={(theme) => ({
                color: theme.palette.navActive ?? theme.palette.primary.dark,
              })}
            >
              <RavenIcon icon={Paperclip} sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          onClick={handlePick}
          startIcon={<RavenIcon icon={CloudUpload} />}
          variant="outlined"
          disabled={uploading}
          sx={(theme) => {
            // Match the active-nav copper used by the AppBar utility
            // actions (Edit / Print) rather than the brighter `primary.main`
            // orange.
            const navActive =
              theme.palette.navActive ?? theme.palette.primary.dark;
            return {
              color: navActive,
              borderColor: alpha(navActive, 0.5),
              "&:hover": {
                borderColor: navActive,
                bgcolor: alpha(navActive, 0.04),
              },
            };
          }}
        >
          {uploading ? "Uploading…" : "Add attachments"}
        </Button>
      )}
      {uploading && <LinearProgress sx={{ mt: 1 }} />}
      {files.length > 0 && (
        <Stack spacing={1} mt={1.5}>
          {files.map((f, idx) => {
            const img = isImage(f);
            return (
              <Stack
                key={`${f.asset_id}-${idx}`}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                }}
              >
                {img && f.previewUrl ? (
                  <Box
                    component="img"
                    src={f.previewUrl}
                    alt={f.name}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      color: "action.active",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <RavenIcon icon={Paperclip} />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {f.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(f.size / 1024).toFixed(1)} KB
                    {f.mime ? ` · ${f.mime}` : ""}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => removeAt(idx)}>
                  <RavenIcon icon={Trash2} sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
